import { createFileRoute, Link } from '@tanstack/react-router';
import {
    BarChart3,
    Check,
    ChevronDown,
    ChevronRight,
    Clock,
    FileText,
    Loader2,
    Users,
    X,
} from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';

import { CaregiverDetailModal } from '@apps/web/components/caregivers/caregiver_detail_modal';
import { AgentRulesCard } from '@apps/web/components/settings/agent_rules_card';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Markdown } from '@apps/web/components/ui/markdown';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@apps/web/components/ui/tabs';
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { cn } from '@apps/web/lib/utils';
import { useDryRunReport } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/(main)/dry-run-report')({
    head: () => ({
        meta: [{ title: 'Simulators | Phoebe' }],
    }),
    component: DryRunReportPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Report = NonNullable<ReturnType<typeof useDryRunReport>['data']>;
type ShiftAnalysis = Report['shifts'][number];

type BaseShift = {
    readonly shift_id: string;
    readonly shift_start: string;
    readonly shift_end: string;
    readonly client_name: string;
    readonly status: string;
    readonly latest_analysis_run_at: string | null;
    readonly latest_analysis_suggestions: ReadonlyArray<{
        readonly caregiver_id: string;
        readonly caregiver_name: string;
        readonly reasoning: string;
        readonly rank: number;
    }> | null;
    readonly has_running_analysis: boolean;
    readonly consistency_score: number | null;
    readonly analysis_run_count: number;
};

type SuggestedCaregiverRow = {
    readonly caregiver_id: string;
    readonly caregiver_name: string;
    readonly rank: number;
    readonly reasons: ReadonlyArray<string>;
};

type SuggestionMap = Readonly<
    Record<string, ReadonlyArray<{ readonly caregiver_id: string }>>
>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDateInputValue(date: Date): string {
    return date.toISOString().slice(0, 10);
}

function formatTimestamp(iso: string) {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function formatDateTime(iso: string) {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function ConsistencyIndicator({
    score,
    runCount,
}: {
    readonly score: number | null | undefined;
    readonly runCount: number;
}) {
    if (runCount < 2 || score === null || score === undefined) {
        if (runCount > 0) {
            return (
                <span
                    className="text-muted-foreground/70"
                    title="Run again to compare"
                >
                    1 run
                </span>
            );
        }
        return null;
    }

    const pct = Math.round(score * 100);
    const color =
        pct >= 80
            ? 'text-green-600'
            : pct >= 50
              ? 'text-amber-600'
              : 'text-red-500';

    return (
        <span
            className={color}
            title={`${pct}% caregiver overlap between last 2 runs (${runCount} total runs)`}
        >
            {pct}%
        </span>
    );
}

function computeMatch(
    shift: ShiftAnalysis,
    analysisSuggestions:
        | ReadonlyArray<{ readonly caregiver_id: string }>
        | undefined,
): boolean | null {
    if (!shift.assigned_caregiver_id) {
        return null;
    }
    if (analysisSuggestions && analysisSuggestions.length > 0) {
        return analysisSuggestions.some(
            (s) => s.caregiver_id === shift.assigned_caregiver_id,
        );
    }
    if (
        shift.latest_analysis_suggestions &&
        shift.latest_analysis_suggestions.length > 0
    ) {
        return shift.latest_analysis_suggestions.some(
            (s) => s.caregiver_id === shift.assigned_caregiver_id,
        );
    }
    if (shift.recommended_caregivers.length > 0) {
        return shift.recommended_caregivers.some(
            (r) => r.caregiver_id === shift.assigned_caregiver_id,
        );
    }
    return shift.assigned_caregiver_recommended ?? null;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function DryRunReportPage() {
    const organizationId = useOrganizationId();

    return (
        <div className="flex h-full flex-col overflow-auto p-6">
            <ShiftAssignmentSimulator organizationId={organizationId} />
        </div>
    );
}

function ShiftAssignmentSimulator({
    organizationId,
}: {
    readonly organizationId: string | null;
}) {
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 14);
        return formatDateInputValue(d);
    });
    const [endDate, setEndDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return formatDateInputValue(d);
    });
    const maxEndDate = (() => {
        const d = new Date();
        d.setDate(d.getDate() - 1);
        return formatDateInputValue(d);
    })();

    const { data: report, isLoading } = useDryRunReport(
        organizationId,
        startDate,
        endDate,
    );
    return (
        <Fragment>
            <div className="mb-6">
                <div className="flex items-center gap-3">
                    <h1 className="text-foreground text-2xl font-semibold">
                        Shift Assignment Simulator
                    </h1>
                    <InstructionsModal organizationId={organizationId} />
                </div>
                <p className="text-muted-foreground mt-1 text-sm">
                    See which caregivers Phoebe would have recommended for each
                    shift
                </p>
            </div>
            <div className="mb-6 flex items-center gap-4">
                <div className="flex items-center gap-2">
                    <label className="text-foreground/80 text-sm font-medium">
                        From
                    </label>
                    <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="w-40"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-foreground/80 text-sm font-medium">
                        To
                    </label>
                    <Input
                        type="date"
                        value={endDate}
                        max={maxEndDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="w-40"
                    />
                </div>
            </div>
            {isLoading ? (
                <div className="flex flex-col items-center gap-3 py-20">
                    <Loader2 className="text-muted-foreground/70 h-6 w-6 animate-spin" />
                    <p className="text-muted-foreground text-sm">
                        Loading shifts and generating recommendations
                    </p>
                </div>
            ) : report ? (
                <Fragment>
                    <SummaryCards report={report} />
                    <Tabs defaultValue="assigned">
                        <TabsList>
                            <TabsTrigger value="assigned">
                                Assigned ({report.shifts.length})
                            </TabsTrigger>
                            <TabsTrigger value="unassigned">
                                Unassigned ({report.unassigned_shifts.length})
                            </TabsTrigger>
                        </TabsList>
                        <TabsContent value="assigned">
                            <AssignedShiftTable shifts={report.shifts} />
                        </TabsContent>
                        <TabsContent value="unassigned">
                            <SimulatorShiftTable
                                shifts={report.unassigned_shifts}
                                title="Unassigned Shifts"
                                subtitle="Shifts with no caregiver assigned — see who Phoebe would suggest"
                                emptyMessage="No unassigned shifts in this date range."
                            />
                        </TabsContent>
                    </Tabs>
                </Fragment>
            ) : (
                <p className="text-muted-foreground py-20 text-center text-sm">
                    Select a date range to generate the report.
                </p>
            )}
        </Fragment>
    );
}

// ---------------------------------------------------------------------------
// Summary Cards
// ---------------------------------------------------------------------------

function SummaryCards({ report }: { readonly report: Report }) {
    const analyzedShifts = report.shifts.filter(
        (s) => (s.latest_analysis_suggestions ?? []).length > 0,
    );
    let matchCount = 0;
    for (const shift of analyzedShifts) {
        const match = computeMatch(shift, undefined);
        if (match === true) {
            matchCount += 1;
        }
    }
    const analyzedCount = analyzedShifts.length;
    const matchPct =
        analyzedCount > 0 ? (matchCount / analyzedCount) * 100 : null;

    return (
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                        Total Shifts
                    </CardTitle>
                    <BarChart3 className="text-muted-foreground h-4 w-4" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold">
                        {report.total_shifts}
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                        With Recommendations
                    </CardTitle>
                    <Users className="text-muted-foreground h-4 w-4" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold">
                        {report.shifts_with_recommendations}
                    </div>
                </CardContent>
            </Card>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                        Phoebe Match Rate
                    </CardTitle>
                    <Check className="h-4 w-4 text-green-500" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold">
                        {matchPct !== null
                            ? `${matchPct.toFixed(0)}%`
                            : '\u2014'}
                    </div>
                    <p className="text-muted-foreground text-xs">
                        {matchCount} / {analyzedCount} analyzed shifts match
                    </p>
                </CardContent>
            </Card>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                        Unassigned Hours
                    </CardTitle>
                    <Clock className="h-4 w-4 text-orange-500" />
                </CardHeader>
                <CardContent>
                    <div className="text-2xl font-bold">
                        {report.total_unassigned_hours.toFixed(0)}h
                    </div>
                    <p className="text-muted-foreground text-xs">
                        {report.total_unassigned_shifts} shifts without
                        caregiver
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}

// ---------------------------------------------------------------------------
// SimulatorShiftTable — shared generic table for both tabs
// ---------------------------------------------------------------------------

function SimulatorShiftTable<T extends BaseShift>({
    shifts,
    title,
    subtitle,
    emptyMessage,
    renderExtraHeaders,
    renderExtraColumns,
    renderTrailingHeaders,
    renderTrailingColumns,
    renderExtraDetail,
    totalColumns,
}: {
    readonly shifts: ReadonlyArray<T>;
    readonly title: string;
    readonly subtitle: string;
    readonly emptyMessage?: string;
    readonly renderExtraHeaders?: () => ReactNode;
    readonly renderExtraColumns?: (
        shift: T,
        suggestionMap: SuggestionMap,
    ) => ReactNode;
    readonly renderTrailingHeaders?: () => ReactNode;
    readonly renderTrailingColumns?: (
        shift: T,
        suggestionMap: SuggestionMap,
    ) => ReactNode;
    readonly renderExtraDetail?: (
        shift: T,
        props: {
            readonly lastAnalysisRunAt: string | null;
        },
    ) => ReactNode;
    readonly totalColumns?: number;
}) {
    const [expandedIdx, setExpandedIdx] = useState<number | null>(null);
    const colCount = totalColumns ?? 6;

    if (emptyMessage && shifts.length === 0) {
        return (
            <p className="text-muted-foreground py-10 text-center text-sm">
                {emptyMessage}
            </p>
        );
    }

    const mergedSuggestions: SuggestionMap = {};

    return (
        <div className="mb-6">
            <div className="mb-3 flex items-center justify-between">
                <div>
                    <h2 className="text-foreground text-lg font-semibold">
                        {title}
                    </h2>
                    <p className="text-muted-foreground text-xs">{subtitle}</p>
                </div>
            </div>
            <div className="overflow-hidden rounded-lg border">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                        <tr>
                            <th className="w-8 px-2 py-2" />
                            <th className="text-foreground/80 px-4 py-2 text-left font-medium">
                                Shift
                            </th>
                            <th className="text-foreground/80 px-4 py-2 text-left font-medium">
                                Client
                            </th>
                            {renderExtraHeaders?.()}
                            <th className="text-foreground/80 px-4 py-2 text-center font-medium">
                                Status
                            </th>
                            <th className="text-foreground/80 px-4 py-2 text-center font-medium">
                                Suggested
                            </th>
                            <th className="text-foreground/80 px-4 py-2 text-center font-medium">
                                Consistency
                            </th>
                            {renderTrailingHeaders?.()}
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {shifts.map((shift, idx) => {
                            const isExpanded = expandedIdx === idx;
                            const hasAnalysis = !!(
                                mergedSuggestions[shift.shift_id] ??
                                shift.latest_analysis_suggestions
                            );
                            return (
                                <Fragment key={shift.shift_id}>
                                    <tr
                                        className="hover:bg-muted/50 cursor-pointer"
                                        onClick={() =>
                                            setExpandedIdx(
                                                isExpanded ? null : idx,
                                            )
                                        }
                                    >
                                        <td className="px-2 py-2 text-center">
                                            {isExpanded ? (
                                                <ChevronDown className="text-muted-foreground/70 inline h-4 w-4" />
                                            ) : (
                                                <ChevronRight className="text-muted-foreground/70 inline h-4 w-4" />
                                            )}
                                        </td>
                                        <td className="px-4 py-2">
                                            <Link
                                                to="/shifts"
                                                search={{
                                                    shiftId: shift.shift_id,
                                                    date: shift.shift_start.split(
                                                        'T',
                                                    )[0],
                                                }}
                                                className="underline decoration-gray-300 hover:decoration-gray-500"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                {formatDateTime(
                                                    shift.shift_start,
                                                )}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-2">
                                            {shift.client_name}
                                        </td>
                                        {renderExtraColumns?.(
                                            shift,
                                            mergedSuggestions,
                                        )}
                                        <td className="px-4 py-2 text-center">
                                            {hasAnalysis ? (
                                                <span className="text-xs text-green-600">
                                                    Analyzed
                                                </span>
                                            ) : (
                                                <span className="text-muted-foreground/70 text-xs">
                                                    Pending
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-muted-foreground px-4 py-2 text-center text-xs">
                                            {(() => {
                                                const count = (
                                                    mergedSuggestions[
                                                        shift.shift_id
                                                    ] ??
                                                    shift.latest_analysis_suggestions
                                                )?.length;
                                                return count !== undefined
                                                    ? count
                                                    : null;
                                            })()}
                                        </td>
                                        <td className="px-4 py-2 text-center text-xs">
                                            <ConsistencyIndicator
                                                score={shift.consistency_score}
                                                runCount={
                                                    shift.analysis_run_count
                                                }
                                            />
                                        </td>
                                        {renderTrailingColumns?.(
                                            shift,
                                            mergedSuggestions,
                                        )}
                                    </tr>
                                    {isExpanded && (
                                        <tr
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <td
                                                colSpan={colCount}
                                                className="bg-muted/50 px-8 py-4"
                                            >
                                                {renderExtraDetail ? (
                                                    renderExtraDetail(shift, {
                                                        lastAnalysisRunAt:
                                                            shift.latest_analysis_run_at ??
                                                            null,
                                                    })
                                                ) : (
                                                    <ShiftAnalysisDetail
                                                        latestAnalysisSuggestions={
                                                            shift.latest_analysis_suggestions
                                                        }
                                                        lastAnalysisRunAt={
                                                            shift.latest_analysis_run_at ??
                                                            null
                                                        }
                                                    />
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// AssignedShiftTable — wraps SimulatorShiftTable with assigned-specific extras
// ---------------------------------------------------------------------------

function AssignedShiftTable({
    shifts,
}: {
    readonly shifts: ReadonlyArray<ShiftAnalysis>;
}) {
    const [modalCaregiverId, setModalCaregiverId] = useState<string | null>(
        null,
    );
    const organizationId = useOrganizationId();

    return (
        <Fragment>
            <SimulatorShiftTable
                shifts={shifts}
                title="Shift-by-Shift Analysis"
                subtitle="Sample of up to 10 shifts with a caregiver assigned"
                totalColumns={8}
                renderExtraHeaders={() => (
                    <th className="text-foreground/80 px-4 py-2 text-left font-medium">
                        Assigned Caregiver
                    </th>
                )}
                renderExtraColumns={(shift) => (
                    <td className="px-4 py-2">
                        {shift.assigned_caregiver_id ? (
                            <button
                                type="button"
                                className="text-left underline decoration-gray-300 hover:decoration-gray-500"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setModalCaregiverId(
                                        shift.assigned_caregiver_id,
                                    );
                                }}
                            >
                                {shift.assigned_caregiver_name}
                            </button>
                        ) : null}
                    </td>
                )}
                renderTrailingHeaders={() => (
                    <th className="text-foreground/80 px-4 py-2 text-center font-medium">
                        Phoebe Match
                    </th>
                )}
                renderTrailingColumns={(shift, suggestionMap) => (
                    <td className="px-4 py-2 text-center">
                        <MatchIndicator
                            recommended={computeMatch(
                                shift,
                                suggestionMap[shift.shift_id],
                            )}
                        />
                    </td>
                )}
                renderExtraDetail={(shift, detailProps) => (
                    <ShiftAnalysisDetail
                        latestAnalysisSuggestions={
                            shift.latest_analysis_suggestions
                        }
                        lastAnalysisRunAt={detailProps.lastAnalysisRunAt}
                        assignedCaregiverId={
                            shift.assigned_caregiver_id ?? undefined
                        }
                        fallbackSuggestions={shift.recommended_caregivers.map(
                            (caregiver) => ({
                                caregiver_id: caregiver.caregiver_id,
                                caregiver_name: caregiver.name,
                                rank: caregiver.rank,
                                reasons: caregiver.reasons,
                            }),
                        )}
                        latestMismatchExplanation={
                            shift.latest_analysis_mismatch_explanation ?? null
                        }
                    />
                )}
            />
            {organizationId && (
                <CaregiverDetailModal
                    isOpen={modalCaregiverId !== null}
                    onClose={() => setModalCaregiverId(null)}
                    organizationId={organizationId}
                    caregiverId={modalCaregiverId ?? undefined}
                />
            )}
        </Fragment>
    );
}

// ---------------------------------------------------------------------------
// ShiftAnalysisDetail — shared detail panel with SSE + polling
// ---------------------------------------------------------------------------

function ShiftAnalysisDetail({
    latestAnalysisSuggestions,
    lastAnalysisRunAt,
    assignedCaregiverId,
    fallbackSuggestions,
    latestMismatchExplanation,
}: {
    readonly latestAnalysisSuggestions: ReadonlyArray<{
        readonly caregiver_id: string;
        readonly caregiver_name: string;
        readonly reasoning: string;
        readonly rank: number;
    }> | null;
    readonly lastAnalysisRunAt: string | null;
    readonly assignedCaregiverId?: string;
    readonly fallbackSuggestions?: ReadonlyArray<SuggestedCaregiverRow>;
    readonly latestMismatchExplanation?: string | null;
}) {
    const displayedSuggestions: ReadonlyArray<SuggestedCaregiverRow> =
        latestAnalysisSuggestions && latestAnalysisSuggestions.length > 0
            ? latestAnalysisSuggestions.map((s) => ({
                  caregiver_id: s.caregiver_id,
                  caregiver_name: s.caregiver_name,
                  rank: s.rank,
                  reasons: s.reasoning ? [s.reasoning] : [],
              }))
            : (fallbackSuggestions ?? []);

    const activeMismatch = latestMismatchExplanation ?? null;

    return (
        <div className="space-y-4">
            <div className="flex items-center gap-3">
                <h4 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
                    Agent Analysis
                </h4>
                {lastAnalysisRunAt ? (
                    <span className="text-muted-foreground text-xs">
                        Last run {formatTimestamp(lastAnalysisRunAt)}
                    </span>
                ) : null}
            </div>
            <div>
                <div className="mb-2 flex items-center gap-2">
                    <h5 className="text-foreground text-sm font-medium">
                        Suggested Caregivers
                    </h5>
                    <Badge variant="outline" className="text-xs">
                        {displayedSuggestions.length}
                    </Badge>
                </div>
                {displayedSuggestions.length > 0 ? (
                    <div className="grid grid-cols-2 gap-1.5 lg:grid-cols-3 xl:grid-cols-4">
                        {displayedSuggestions.map((suggestion) => {
                            const isAssigned =
                                assignedCaregiverId !== undefined &&
                                suggestion.caregiver_id === assignedCaregiverId;
                            return (
                                <div
                                    key={suggestion.caregiver_id}
                                    className={cn(
                                        'flex items-center gap-2 rounded border px-2.5 py-1.5',
                                        isAssigned
                                            ? 'border-green-400 bg-green-50 dark:border-green-700 dark:bg-green-950/50'
                                            : 'bg-background',
                                    )}
                                >
                                    <span className="bg-muted text-muted-foreground flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold">
                                        {suggestion.rank}
                                    </span>
                                    <span className="text-foreground min-w-0 truncate text-xs font-medium">
                                        {suggestion.caregiver_name}
                                    </span>
                                    {suggestion.reasons.length > 0 && (
                                        <ReasonPill
                                            reasons={suggestion.reasons}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="text-muted-foreground/70 text-xs">
                        No caregiver suggestions available for this shift.
                    </p>
                )}
            </div>
            {activeMismatch && <MismatchSection explanation={activeMismatch} />}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Small shared components
// ---------------------------------------------------------------------------

function ReasonPill({ reasons }: { readonly reasons: ReadonlyArray<string> }) {
    return (
        <Tooltip>
            <TooltipTrigger asChild={true}>
                <button
                    type="button"
                    className="border-border bg-muted text-foreground/80 cursor-help rounded-full border px-2.5 py-0.5 text-xs font-medium"
                >
                    Reasoning
                </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-md text-left">
                <Markdown className="text-primary-foreground text-xs">
                    {reasons.join('\n\n')}
                </Markdown>
            </TooltipContent>
        </Tooltip>
    );
}

function MismatchSection({ explanation }: { readonly explanation: string }) {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/50">
            <button
                type="button"
                className="flex w-full items-center gap-2 p-3 text-left"
                onClick={() => setExpanded((prev) => !prev)}
            >
                {expanded ? (
                    <ChevronDown className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                ) : (
                    <ChevronRight className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
                )}
                <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                    Why wasn{'\u2019'}t the assigned caregiver suggested?
                </span>
            </button>
            {expanded && (
                <div className="px-3 pb-3">
                    <Markdown className="text-xs text-amber-900 dark:text-amber-200">
                        {explanation}
                    </Markdown>
                </div>
            )}
        </div>
    );
}

function MatchIndicator({
    recommended,
}: {
    readonly recommended: boolean | null | undefined;
}) {
    if (recommended === null || recommended === undefined) {
        return null;
    }
    if (recommended) {
        return <Check className="inline h-4 w-4 text-green-600" />;
    }
    return <X className="inline h-4 w-4 text-red-400" />;
}

function InstructionsModal({
    organizationId,
}: {
    readonly organizationId: string | null;
}) {
    return (
        <Dialog>
            <DialogTrigger asChild={true}>
                <Button variant="outline" size="sm" className="gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    Instructions
                </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[80vh] overflow-y-auto sm:!max-w-4xl">
                <DialogHeader>
                    <DialogTitle className="sr-only">
                        Agent Instructions
                    </DialogTitle>
                </DialogHeader>
                <AgentRulesCard organizationId={organizationId} />
            </DialogContent>
        </Dialog>
    );
}
